import { ContactAddressType, ExternalNumber } from '../common/ContactAddress.type'

// 電話番号ラベル種別コード（PhoneLabelItems.ts と対応）
const LABEL_MAIN = 1 // メイン
const LABEL_OFFICE = 2 // 勤務先
const LABEL_MOBILE = 4 // 携帯

// 空白を除去した有効な外部電話番号のみを返す
const validExternalNumbers = (
  contact: Pick<ContactAddressType, 'externalNumbers'>,
): ExternalNumber[] =>
  (contact.externalNumbers ?? [])
    .map((r) => ({
      ...r,
      externalPhoneNumber: (r.externalPhoneNumber ?? '').trim(),
    }))
    .filter((r) => r.externalPhoneNumber !== '')

/**
 * 連絡先の「代表電話番号」を1件選ぶ。
 * 画面（連絡先カード）とCSVエクスポートで共通利用する。
 * 選定順：
 *   ① 旧来の電話番号（phone / PublicNumber）
 *   ② 外部番号のうち「メイン(1)」
 *   ③ 外部番号のうち「携帯(4)」
 *   ④ 外部番号のうち displayOrder 最小（先頭）
 */
export const pickRepresentativePhone = (
  contact: Pick<ContactAddressType, 'phone' | 'externalNumbers'>,
): string => {
  // ① 旧来の電話番号
  const legacy = (contact.phone ?? '').trim()
  if (legacy !== '') return legacy

  const valid = validExternalNumbers(contact)
  if (valid.length === 0) return ''

  // ② メイン(1)
  const main = valid.find((r) => r.labelCode === LABEL_MAIN)
  if (main) return main.externalPhoneNumber

  // ③ 携帯(4)
  const mobile = valid.find((r) => r.labelCode === LABEL_MOBILE)
  if (mobile) return mobile.externalPhoneNumber

  // ④ displayOrder 最小（先頭）
  const sorted = valid
    .slice()
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))

  return sorted[0]?.externalPhoneNumber ?? ''
}

/**
 * 指定した種別(labelCode)の外部電話番号を1件返す（無ければ空文字）。
 * 同一種別が複数ある場合は displayOrder 最小を優先する。
 */
export const pickPhoneByLabel = (
  contact: Pick<ContactAddressType, 'externalNumbers'>,
  labelCode: number,
): string => {
  const found = validExternalNumbers(contact)
    .filter((r) => r.labelCode === labelCode)
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))

  return found[0]?.externalPhoneNumber ?? ''
}

// 勤務先(2)番号の種別コードを外部公開（呼び出し側の可読性向上のため）
export const LABEL_CODE_OFFICE = LABEL_OFFICE
